import { Column, Divider, Row, Text } from '~/commonComponents';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { ChatOutlineIcon, ChevronRight, ShareIcon } from '~/images/icons';
import UserIcon from '~/images/user-icon.svg';
import { Colors } from '~/themes/colors';
import type { IOnsiteInterviewCardProps } from './interfaces';
import { ClipLoader } from 'react-spinners';

export const OnsiteInterviewCard = ({
  candidatesAppliedCount,
  date,
  endsAt,
  isInvited = false,
  isInviting = false,
  onsiteInterviewId,
  profession,
  rate,
  specialty,
  startsAt,
  subProfession = '',
  variant,
  onConfirm,
  onDecline,
  onInvite,
  onMessage,
  onViewDetails,
}: IOnsiteInterviewCardProps) => {
  const hasMultipleCandidates = candidatesAppliedCount ? candidatesAppliedCount > 1 : false;
  const isAppliedVariant = variant === 'applied';
  const isAvailableVariant = variant === 'available';
  const isCompletedVariant = variant === 'completed';
  const isScheduledVariant = variant === 'scheduled';

  return (
    <Column
      backgroundColor={Colors.white}
      borderRadius={8}
      className="cursor-pointer shadow-xs"
      data-cy={`onsite-interview-card-${onsiteInterviewId}`}
      paddingBlock={18}
      paddingInline={24}
      onClick={() => onViewDetails?.(onsiteInterviewId)}
    >
      <Row alignItems="center" justifyContent="space-between">
        <Text bold fontSize={18}>
          {date}
        </Text>
        <Row cursor="pointer" role="button">
          <Text bold color={Colors.secondary_500} fontSize={18}>
            View Details
          </Text>
          <ChevronRight color={Colors.secondary_500} size={24} />
        </Row>
      </Row>
      <Divider color={Colors.neutral_100_divider} height={1} marginVertical={8} />
      <Row alignItems="flex-end" gap={4}>
        <Column flex={1} marginTop={4}>
          <Text>
            {UserSubProfession.getName(subProfession) || UserProfession.getName(profession)} /{' '}
            {specialty}
          </Text>
          <Text>
            {startsAt} – {endsAt} &bull; ${rate?.toFixed(2)}/hr
          </Text>
        </Column>
        {(isAppliedVariant || isAvailableVariant) && hasMultipleCandidates && (
          <Row alignItems="center" justifyContent="center" gap={4}>
            <img alt="user-icon" src={UserIcon} height={24} width={24} />
            <Text bold color={Colors.secondary_500} fontSize={20}>
              {candidatesAppliedCount}
            </Text>
          </Row>
        )}
      </Row>
      {!isCompletedVariant && (
        <Divider color={Colors.neutral_100_divider} height={1} marginVertical={8} />
      )}
      <Row alignItems="center" gap={16} justifyContent="center" marginTop={6}>
        {isAppliedVariant && (
          <>
            <button
              className="shadow-xs hover__shadow-sm"
              data-cy="candidate-action-decline"
              style={{
                alignItems: 'center',
                background: Colors.secondary_500,
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDecline?.(onsiteInterviewId);
              }}
            >
              <Text bold color={Colors.white} fontSize={18}>
                Decline
              </Text>
            </button>
            <button
              className="shadow-xs hover__shadow-sm"
              data-cy="candidate-action-confirm"
              style={{
                alignItems: 'center',
                background: Colors.primary_500,
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onConfirm?.(onsiteInterviewId);
              }}
            >
              <Text bold color={Colors.white} fontSize={18}>
                Confirm
              </Text>
            </button>
          </>
        )}
        {isAvailableVariant && (
          <button
            className="shadow-xs hover__shadow-sm"
            data-cy="candidate-action-invite"
            disabled={isInvited}
            style={{
              alignItems: 'center',
              background: isInvited ? Colors.primary_200 : Colors.primary_500,
              borderRadius: 48,
              cursor: isInvited ? 'default' : undefined,
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              paddingBlock: 14,
              paddingInline: 16,
              width: 140,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onInvite?.(onsiteInterviewId);
            }}
          >
            {isInviting ? (
              <ClipLoader color={Colors.white} size={16} />
            ) : (
              <>
                <ShareIcon color={Colors.white} />
                <Text bold color={Colors.white} fontSize={18}>
                  {isInvited ? 'Invited' : 'Invite'}
                </Text>
              </>
            )}
          </button>
        )}
        {isScheduledVariant && (
          <button
            className="shadow-xs hover__shadow-sm"
            data-cy="candidate-action-message"
            style={{
              alignItems: 'center',
              background: Colors.primary_500,
              borderRadius: 48,
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              paddingBlock: 14,
              paddingInline: 16,
              width: 140,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMessage?.(onsiteInterviewId);
            }}
          >
            <ChatOutlineIcon color={Colors.white} />
            <Text bold color={Colors.white} fontSize={18}>
              Message
            </Text>
          </button>
        )}
      </Row>
    </Column>
  );
};
